import { useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, ChevronDown, Send, X } from 'lucide-react';
import { studioInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Studio() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', brief: '' });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brief) {
      toast({ title: 'Missing info', description: 'Please share your name, email, and a short note.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/founder`, form, { timeout: 25000 });
      try {
        const saved = JSON.parse(localStorage.getItem('smg_founder_inquiries') || '[]');
        saved.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem('smg_founder_inquiries', JSON.stringify(saved));
      } catch {}
      toast({
        title: 'Sent to the founder',
        description: 'Routed directly to Brian. Reply within 48h.',
      });
      setForm({ name: '', email: '', phone: '', brief: '' });
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'please try again in a moment';
      toast({ title: 'Could not send', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="studio" className="relative px-5 md:px-10 lg:px-14 py-24 md:py-32 border-t border-white/5">
      <div className="flex items-center gap-4 mb-10">
        <span className="w-10 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/70">04 / THE STUDIO</p>
      </div>

      <h2 className="font-display text-[8vw] md:text-[5vw] lg:text-[5rem] leading-[0.95] tracking-tight mb-16">
        <span className="text-white">It starts with</span>
        <br />
        <span className="text-white">an </span>
        <span className="font-script text-white/40">obsessed leader.</span>
      </h2>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        {/* Portrait */}
        <div className="md:col-span-6 relative">
          <div className="group relative aspect-[4/5] bg-neutral-950 overflow-hidden">
            <img
              src="/assets/brian_portrait_jacket.png?v=2"
              alt="Brian Stretch — Founder, SMG Media Group"
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 scale-100 group-hover:scale-[1.03] transition-[filter,transform] duration-700 ease-out"
              draggable={false}
            />
            {/* Subtle gradient + grain overlays for brand consistency */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-25 pointer-events-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
              }}
            />
            <div className="absolute bottom-5 left-5 text-[10px] tracking-[0.3em] text-white/85 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3D2E] animate-pulse" /> 01 — BRIAN STRETCH
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-6 flex flex-col">
          <p className="text-[10px] tracking-[0.3em] text-white/55 mb-4">{studioInfo.role}</p>
          <h3 className="font-display text-5xl md:text-6xl tracking-tight text-white mb-2">{studioInfo.name}</h3>
          <p className="text-[11px] tracking-[0.3em] text-[#FF3D2E] mb-10">{studioInfo.title}</p>

          <blockquote className="font-script text-2xl md:text-3xl text-white/90 leading-snug mb-10 border-l-2 border-[#FF3D2E] pl-5">
            “{studioInfo.quote}”
          </blockquote>

          <div className="grid grid-cols-2 gap-6 border-t border-white/10 pt-6 mb-8">
            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/45 mb-2">BASED</p>
              <p className="text-white">{studioInfo.based}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/45 mb-2">FOCUS</p>
              <p className="text-white">{studioInfo.focus}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {studioInfo.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.25em] text-white/70 border border-white/15 px-3 py-1.5"
              >
                {t}
              </span>
            ))}
          </div>

          {/* Drop-form trigger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={`group inline-flex items-center justify-between gap-2 px-6 py-4 border text-[11px] tracking-[0.25em] transition-colors w-full md:w-fit ${
              open
                ? 'border-[#FF3D2E] bg-[#FF3D2E]/10 text-white'
                : 'border-white/30 text-white hover:bg-white hover:text-black'
            }`}
          >
            <span className="inline-flex items-center gap-2">
              {open ? 'CLOSE' : 'WORK DIRECTLY WITH THE FOUNDER'}
              {open ? (
                <X className="w-3.5 h-3.5" />
              ) : (
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              )}
            </span>
            <ChevronDown
              className={`w-4 h-4 ml-2 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Drop-form panel */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-out ${
              open ? 'max-h-[1200px] opacity-100 mt-5' : 'max-h-0 opacity-0 mt-0'
            }`}
          >
            <form
              onSubmit={submit}
              className="border border-white/15 bg-white/[0.02] p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] tracking-[0.3em] text-white/55">
                  DIRECT LINE — <span className="text-[#FF3D2E]">TO THE FOUNDER'S INBOX</span>
                </p>
                <span className="text-[10px] tracking-[0.3em] text-white/30">PRIVATE</span>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="NAME">
                  <input
                    type="text"
                    value={form.name}
                    onChange={onChange('name')}
                    placeholder="Full name"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-2.5 text-white placeholder:text-white/30 transition-colors"
                  />
                </Field>
                <Field label="EMAIL">
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange('email')}
                    placeholder="name@brand.com"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-2.5 text-white placeholder:text-white/30 transition-colors"
                  />
                </Field>
                <Field label="PHONE (OPTIONAL)" className="md:col-span-2">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={onChange('phone')}
                    placeholder="+1 555 000 0000"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-2.5 text-white placeholder:text-white/30 transition-colors"
                  />
                </Field>
                <Field label="WHAT ARE YOU BUILDING?" className="md:col-span-2">
                  <textarea
                    rows={3}
                    value={form.brief}
                    onChange={onChange('brief')}
                    placeholder="Brand, goal, timeline — keep it short. Brian replies fast."
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-2.5 text-white placeholder:text-white/30 resize-none transition-colors"
                  />
                </Field>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[10px] tracking-[0.3em] text-white/40">
                  REPLY WITHIN <span className="text-white/70">48H</span>
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-[#FF3D2E] text-white text-[11px] tracking-[0.3em] hover:bg-[#ff5446] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'SENDING…' : 'SEND TO BRIAN'}
                  <Send className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] tracking-[0.3em] text-white/45 mb-1">{label}</span>
      {children}
    </label>
  );
}
